import React, { useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch, SearchBox, RefinementList, Configure } from "react-instantsearch";
import InfiniteHits from "./search/infinite_hits";
import SearchToolbar from "../source_directory/SearchToolbar";
import FilterPill from "../source_directory/FilterPill";
import FilterClearEffect from "../source_directory/FilterClearEffect";
import LocationFilter from "../source_directory/LocationFilter";
import sharedAlgoliaRouter from "../source_directory/sharedAlgoliaRouter";
import useSavedOnlyFilter from "../source_directory/useSavedOnlyFilter";
import useDebouncedQueryHook from "../source_directory/useDebouncedQueryHook";

// The public-pitch index has no show_in_default_index attribute, so this tab uses a plain
// Configure for the saved-only filter rather than the promo-entity tabs' DefaultIndexConfigure.
function StoryIdeasSearch({
  algoliaAppId,
  algoliaSearchKey,
  indexName,
  viewMode,
  showFilters,
  onToggleFilters,
  savedOnly,
  onToggleSavedOnly,
  onViewModeChange,
  groupCount,
  onMyGroupsClick,
}) {
  const queryHook = useDebouncedQueryHook();
  const searchClient = useMemo(() => algoliasearch(algoliaAppId, algoliaSearchKey), [algoliaAppId, algoliaSearchKey]);
  const savedFilter = useSavedOnlyFilter(savedOnly, "Enrich::PublicPitch");

  const getSearchParamsFromUrl = () => {
    const url = new URL(window.location.href);
    return {
      query: url.searchParams.get("query"),
      sourceName: url.searchParams.get("source_name"),
      publicationName: url.searchParams.get("publication_name"),
      companyName: url.searchParams.get("company_name"),
      title: url.searchParams.get("title"),
      gender: url.searchParams.get("gender"),
      locationName: url.searchParams.get("location_name"),
      locationRadius: url.searchParams.get("location_radius"),
    };
  };

  const transformGenders = (inputString) => {
    const mappings = {
      female: "Female",
      male: "Male",
      nonbinary: "Non-binary +",
      notspecified: "Not specified",
    };
    return inputString
      .split(",")
      .map((gender) => mappings[gender])
      .filter(Boolean);
  };

  // Rewrite friendly deep-link params into Algolia's routing format before InstantSearch reads the
  // URL on mount (must happen during render, not in a useEffect).
  const didTransformUrl = useRef(false);
  if (!didTransformUrl.current) {
    didTransformUrl.current = true;
    const currentUrl = new URL(window.location.href);
    const searchParams = getSearchParamsFromUrl();
    const paramsToTransform = ["query", "sourceName", "publicationName", "companyName", "title", "gender"];
    const needsTransformation = paramsToTransform.some((param) => searchParams[param] !== null);
    const hasAlgoliaParams = Array.from(currentUrl.searchParams.keys()).some((key) => key.includes(`${indexName}[`));

    if (needsTransformation && !hasAlgoliaParams) {
      const newUrl = new URL(window.location.href);
      // Drop only the consumed params; leave the rest of the URL (saved=1, other tabs' state) intact.
      ["query", "source_name", "publication_name", "company_name", "title", "gender"].forEach((param) =>
        newUrl.searchParams.delete(param),
      );
      if (searchParams.query) newUrl.searchParams.set(`${indexName}[query]`, searchParams.query);
      if (searchParams.sourceName)
        newUrl.searchParams.set(`${indexName}[refinementList][expert.name][0]`, searchParams.sourceName);
      if (searchParams.publicationName)
        newUrl.searchParams.set(`${indexName}[refinementList][publication.name][0]`, searchParams.publicationName);
      if (searchParams.companyName)
        newUrl.searchParams.set(`${indexName}[refinementList][expert.org_name][0]`, searchParams.companyName);
      if (searchParams.title)
        newUrl.searchParams.set(`${indexName}[refinementList][expert.job_title][0]`, searchParams.title);
      if (searchParams.gender) {
        const genders = transformGenders(searchParams.gender);
        genders.forEach((gender, index) => {
          newUrl.searchParams.set(`${indexName}[refinementList][gender][${index}]`, gender);
        });
      }
      window.history.replaceState(null, "", newUrl.toString());
    }
  }

  const { locationName, locationRadius } = getSearchParamsFromUrl();
  const [locationResetSignal, setLocationResetSignal] = useState(0);

  return (
    <div className="ais-InstantSearch">
      <InstantSearch
        indexName={indexName}
        searchClient={searchClient}
        routing={{ router: sharedAlgoliaRouter }}
        future={{ preserveSharedStateOnUnmount: true, cleanUrlOnDispose: false }}
      >
        <Configure filters={savedFilter || ""} />
        <FilterClearEffect showFilters={showFilters} onClear={() => setLocationResetSignal((signal) => signal + 1)} />
        <SearchToolbar
          showFilters={showFilters}
          onToggleFilters={onToggleFilters}
          savedOnly={savedOnly}
          onToggleSavedOnly={onToggleSavedOnly}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          groupCount={groupCount}
          onMyGroupsClick={onMyGroupsClick}
        >
          <SearchBox autoFocus placeholder="Search" searchAsYouType queryHook={queryHook} />
        </SearchToolbar>

        <div
          className="sd-filter-row"
          style={{ display: showFilters ? "flex" : "none", flexWrap: "wrap", gap: 8, marginBottom: 12 }}
        >
          <FilterPill label="Source" attribute="expert.name">
            <RefinementList attribute="expert.name" limit={10} showMore searchable />
          </FilterPill>
          <FilterPill label="Publication" attribute="publication.name">
            <RefinementList attribute="publication.name" limit={10} showMore searchable />
          </FilterPill>
          <FilterPill label="Company" attribute="expert.org_name">
            <RefinementList attribute="expert.org_name" limit={10} showMore searchable />
          </FilterPill>
          <FilterPill label="Title" attribute="expert.job_title">
            <RefinementList attribute="expert.job_title" limit={10} showMore searchable />
          </FilterPill>
          <FilterPill label="Gender" attribute="gender">
            <RefinementList
              attribute="gender"
              limit={6}
              showMore={false}
              searchable={false}
              transformItems={(items) => [...items].sort((a, b) => a.label.localeCompare(b.label))}
            />
          </FilterPill>
          <LocationFilter
            initialLocationName={locationName}
            initialLocationRadius={locationRadius}
            resetSignal={locationResetSignal}
          />
        </div>

        <InfiniteHits viewMode={viewMode} />
      </InstantSearch>
    </div>
  );
}

StoryIdeasSearch.propTypes = {
  algoliaAppId: PropTypes.string.isRequired,
  algoliaSearchKey: PropTypes.string.isRequired,
  indexName: PropTypes.string.isRequired,
  viewMode: PropTypes.oneOf(["grid", "list"]).isRequired,
  showFilters: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  savedOnly: PropTypes.bool,
  onToggleSavedOnly: PropTypes.func.isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  groupCount: PropTypes.number,
  onMyGroupsClick: PropTypes.func,
};

export default StoryIdeasSearch;
